import Vue from "vue";
import { firestore, storage } from "@/firebase";
import { first } from "rxjs/operators";
import { splitPath, pathHead, mountNotes } from "../helpers.js";
import { cache } from "./helpers/actions.store";
import { set_fetch_timestamp } from "./helpers/mutations.store";

const namespaced = true;

export const getters = {
  notes: state => state.notes,
  all: (state, getters, rootState, rootGetters) => template_id => {
    const template = rootGetters["templates/template"](template_id);
    return mountNotes(state.notes, template);
  },
  stored: (state, getters, rootState, rootGetters) =>
    getters.is_watching && rootGetters["templates/template"](state.contextName),
  
  cached: (state, getters, rootState, rootGetters) =>
    mountNotes(state.notes, rootGetters["templates/template"](state.contextName)),
  
  ref: state => firestore.collection(state.ref_path),
  is_watching: state => state.notes_listener ? true : false
};

export const mutations = {
  set_note(state, payload) {
    const obj = {
      id: payload.id,
      ...payload.data()
    };
    let index = state.notes.findIndex(note => note.id === payload.id);
    if (index < 0) index = state.notes.length;
    Vue.set(state.notes, index, obj);
  },
  set_notes_listener(state, subject) { state.notes_listener = subject; },
  clear_notes(state) { state.notes = []; },
  set_fetch_timestamp
};

const actions = {
  async get({ dispatch, state, getters }, payload) {
    await dispatch("cache", payload?.options);
    await dispatch("templates/cache", { path: state.contextName }, { root: true })
    if (pathHead(payload?.path))
      console.warn("IMPLEMENT: other options for action get in payload.");
    else
      return getters.cached;
  },
  async fetch({ commit, dispatch }) {
    try {
      await dispatch("fetch_notes");
      commit("set_fetch_timestamp");
    } catch (error) {
      console.error(error);
      throw Error("An error occurred while fetching the context doc and/or the items of the context.");
    }
  },
  async watch({ dispatch, commit }) {
    try {
      await dispatch("watch_notes");
      commit("set_fetch_timestamp");
    } catch (error) {
      console.error(error);
      throw Error("An error occurred while watching the context doc and/or the items of the context.");
    }
  },
  async fetch_notes({ dispatch, getters }) {
    try {
      const notes_snapshot = await getters.ref.orderBy("time", "desc").get();
      dispatch("set_notes", notes_snapshot);
    } catch (error) {
      console.error(error);
      throw new Error("An error occurred while fetching the context's items collection from the database.");
    }
  },
  async watch_notes({ dispatch, commit, getters }) {
    const subject = await dispatch("attatch_listener", {
      ref: getters.ref,
      mutation: snapshot => dispatch("set_notes", snapshot)
    }, { root: true });
    commit("set_notes_listener", subject);
    await subject.pipe(first()).toPromise();
    return getters.cached;
  },
  async delete({ getters, dispatch }, note) {
    const ref = getters.ref.doc(note.id);
    const promises = [ref.delete()];
    if (note.fileURL) {
      promises.push(storage.refFromURL(note.fileURL).delete());
    }
    
    try {
      await Promise.all(promises);
      const parts = splitPath(note.item_paths[0]);
      const template = await dispatch("templates/get", { path: parts[0]}, { root: true});
      return mountNotes(getters.notes, template);
    } catch (error) {
      console.error("An error occurred while trying to delete a note and/or its file.");
      throw error;
    }
  },
  set_notes({ commit }, snapshot) {
    commit("clear_notes");
    snapshot.docs.forEach(doc => commit("set_note", doc));
  },
  cache
};

export const state = (moduleName, contextName, ref) => ({
  moduleName,
  contextName,
  ref_path: ref.path,
  notes: [],
  notes_listener: null,
  lastFetched: "never"
});

export default (moduleName, contextName, ref) => ({
  namespaced,
  state: state(moduleName, contextName, ref),
  getters,
  mutations,
  actions
});

